<template>

    <v-dialog
        v-model="dialogModel"
        max-width="1200px"
        persistent
        eager
    >

        <toolbar-page :title="title" icon="mdi-security">
            <template slot="buttons">
                <v-btn @click="closeDialog" icon>
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </toolbar-page>

        <v-form v-model="valid" ref="form" id="form">
            <v-card width="100%">
                <v-card-text>

                    <v-container fluid class="pa-0">
                        <v-row no-gutters>
                            <v-col cols="12" class="d-flex">
                                <v-text-field
                                    class="align-self-center"
                                    label="Role name"
                                    v-model="form.data.name"
                                    :counter="60"
                                    :rules="nameRules"
                                    required
                                    :error-messages="form.getError('name')"
                                    prepend-icon="mdi-security"
                                    validate-on-blur
                                    style="max-width: 500px"
                                ></v-text-field>

                                <v-btn class="ml-6 mb-2 align-self-center"
                                       color="primary"
                                       @click="saveRole"
                                       :loading="saving"
                                       :disabled="submitButtonDisabled">
                                    {{ this.isEditing ? 'Update Role' : 'Create Role' }}
                                </v-btn>

                            </v-col>

                        </v-row>

                    </v-container>

                    <div class="mt-2">
                        <h3>Permissions</h3>
                    </div>

                    <div style="width:100%;" ref="roleContainer">

                        <div
                            class="d-flex flex-wrap align-start flex-column rolesScrollHorizontally pb-4"
                            :style="roleContainerStyle">

                            <div v-for="permission in form.data.permissions"
                                 :key="permission.id"
                                 class="mr-2 pl-2 pr-2 mt-1">

                                <div class="d-flex">
                                    <v-checkbox
                                        hide-details
                                        class="mt-0 align-self-center"
                                        :label="permission.name.split(' ').map(word => ucFirst(word)).join(' ')"
                                        v-model="permission.selected"
                                        @change="permissionChecked"
                                    ></v-checkbox>

                                    <dangerous-permission-icon class="align-self-center" v-if="dangerousPermissions.includes(permission.name)"/>

                                </div>

                            </div>
                        </div>
                    </div>

                </v-card-text>

            </v-card>
        </v-form>

    </v-dialog>

</template>

<script>

import {mapActions} from 'vuex';
import Form from '../../../../components/forms/Form';
import ToolbarPage from '../../../../components/UI/ToolbarPage';
import Json from 'vue-json-pretty';
import {dangerousPermissionsMixin} from "../../../../components/mixins/dangerousPermissionsMixin";
import DangerousPermissionIcon from "../DangerousPermissionIcon";

const vuexStore = 'UserManager';

const adminPermissions = [
    'invite users',
    'update users',
    'delete users',
    'create roles',
    'update roles',
    'delete roles'
];

export default {

    mixins: [dangerousPermissionsMixin],

    components: {
        DangerousPermissionIcon,
        ToolbarPage,
        Json
    },

    props: {
        value: {
            type: Boolean,
            required: true
        },
        role: {
            type: Object,
            required: false,
        },
        permissions: {
            type: Array,
            required: true,
        },
        errorMessages: {
            type: Object,
            required: false,
        },
        loading: {
            type: Boolean,
            required: false,
            default: false,
        }
    },

    data() {
        return {
            form: new Form({
                name: '',
                permissions: []
            }),
            valid: false,
            saving: false,
            nameRules: [
                (v) => !!v && v.length > 0 || 'Role name is required'
            ],
            window: {
                width: null,
                height: null,
            },
            roleContainer: {
                width: null,
                height: null,
            },
        }
    },

    computed: {

        dialogModel: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },

        isEditing() {
            return this.role !== null;
        },

        title() {
            return this.isEditing ? 'Edit Role' : 'Create new role'
        },

        submitButtonDisabled() {
            if (!this.hasPermission('create roles')) return true;
            if (this.form.busy) return true;
            if (this.form.data.name.length < 3) return true;

            return this.form.data.permissions.filter(permission => permission.selected).length === 0;
        },

        roleContainerStyle() {
            return "width: " + this.roleContainer.width + "px; height: " + this.roleContainer.height + "px;";
        }
    },

    mounted() {
        window.addEventListener('resize', () => {
            this.handleResize();
        });

        this.prepareSelected();
    },

    watch: {
        value(dialogOpen) {
            if (dialogOpen) {
                this.handleResize();
                this.prepareSelected();
            }
        },
        permissions() {
            this.prepareSelected();
        }
    },

    methods: {
        ...mapActions(vuexStore, [
            'createRole',
            'updateRole'
        ]),

        permissionChecked() {
            if (this.form.data.permissions.filter(item => item.selected).length === 0) {
                Bus.$emit('flash-message', {
                    type: 'error',
                    message: 'Each role must have at least one permission checked'
                });
            }
        },

        reportHeight() {
            this.roleContainer.height = this.window.height * 0.6;
            this.roleContainer.width = this.$refs.roleContainer.clientWidth;
        },

        handleResize() {
            this.window.width = window.innerWidth;
            this.window.height = window.innerHeight;
            this.$nextTick(() => {
                this.setRoleContainerSize();
            });
        },

        setRoleContainerSize() {
            this.roleContainer.height = this.window.height * 0.6;
            this.roleContainer.width = this.$refs.roleContainer.clientWidth;
        },

        prepareSelected() {

            if (!this.permissions) return;

            if (this.isEditing) {

                this.form.data.name = this.role.name;
                this.form.data.permissions = this.filterAdminPermissionOut(this.checkSelected());

            } else {

                this.form.data.permissions = this.filterAdminPermissionOut(this.permissions);
            }
        },

        checkSelected() {
            return this.permissions.map((item) => {
                item.selected = !!this.role.permissions.find(permission => permission.name === item.name);
                return item;
            });
        },

        filterAdminPermissionOut(permissions) {
            return permissions.filter(permission => !adminPermissions.includes(permission.name));
        },

        saveRole() {

            this.saving = true;

            if (this.isEditing) {

                let data = {
                    id: this.role.id,
                    name: this.form.data.name,
                    permissions: this.form.data.permissions
                        .filter(permission => permission.selected === true)
                        .map(permission => permission.name)
                };

                this.updateRole(data)
                    .then(() => {
                        Bus.$emit('flash-message', {
                            message: data.name + ' role successfully updated!'
                        })
                        this.closeDialog();
                    })
                    .catch((error) => {
                        if (error.response.status === 422) {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'Role name already exists'
                            })
                        }
                    })
                    .finally(() => {
                        this.saving = false;
                    });

            } else {

                let data = {
                    name: this.form.data.name,
                    permissions: this.form.data.permissions
                        .filter(permission => permission.selected === true)
                        .map(permission => permission.name)
                };

                this.createRole(data)
                    .then(() => {
                        Bus.$emit('flash-message', {
                            message: data.name + ' role successfully saved!'
                        })
                        this.closeDialog();
                    })
                    .catch((error) => {
                        if (error.response.status === 422) {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'Role name already exists'
                            })
                        }
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            }
        },

        closeDialog() {
            this.form.data.permissions = [];
            this.form.data.name = '';
            this.$emit('input', false);
        },
    },
}
</script>

<style scoped>

.rolesScrollHorizontally {
    overflow-x: auto;
    width: 100%;
}

</style>